import { reduce, result, map, isString, every } from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  AccordionCard,
  FormCardDefaultHeader,
  FormCardBody,
} from '@bingads-webui-fluent-component/form-cards';
import { Input, Label, Button, Dropdown, Option, useRestoreFocusTarget, Tooltip } from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { WebpageCondition, DsaTarget } from '@bingads-webui/campaign-odata-schemas';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { withError, withValidation } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  DSA_TARGET_OPERAND_TYPES,
  DSA_TARGET_OPERAND_TYPE_STRINGS,
  DSA_SPECIFIC_TARGET_OPERATOR_MAP,
  generateKeyAndFormateDsaTarget,
  targetingSourceOptions,
} from '@bingads-webui-campaign-react/search-campaign-data';

import { DefaultBidDescription } from './default-bid-description';
import { EditableFluentList } from '../fluent-list-component';
import { getRuleStyles, getCategoryRuleStyles } from './styles/rule-style';
import { ValidatedCategorySelector } from './category/category-selector';
import { useCurrencyCode, useDefaultBid } from '../utils';

const RULE_TYPES = [
  DSA_TARGET_OPERAND_TYPES.Url,
  DSA_TARGET_OPERAND_TYPES.Category,
  DSA_TARGET_OPERAND_TYPES.PageTitle,
  DSA_TARGET_OPERAND_TYPES.PageContent,
  DSA_TARGET_OPERAND_TYPES.CustomLabel,
];

const ValidatedTextInput = withControlErrorHandling(withUnvalidatedControlErrors(withError(withValidation(Input))));

const CategoryRule = ({
  condition, preloadedCategories, domainName, languageCode, onValidateArgumentOperand,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getCategoryRuleStyles);

  return (
    <div className={classes.root}>
      <div className={classes.label}>{i18n.getString(_TL_('Category'))}</div>
      <div className={classes.selector}>
        <ValidatedCategorySelector
          validationProps={{
            data: condition,
            onValidate: onValidateArgumentOperand,
            field: 'ArgumentOperand',
            schema: WebpageCondition,
          }}
          preloadedCategories={preloadedCategories}
          domainName={domainName}
          languageCode={languageCode}
          i18n={i18n}
        />
      </div>
    </div>
  );
};

CategoryRule.propTypes = {
  condition: PropTypes.shape({}).isRequired,
  preloadedCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  domainName: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  onValidateArgumentOperand: PropTypes.func.isRequired,
};

const SingleRule = ({
  condition, i18n, activity, onChangeRule, onRemoveRule, alwaysShowError, preloadedCategories, domainName, languageCode, selectedTargetingSource,
}) => {
  const selectedRuleType = condition.Operand || DSA_TARGET_OPERAND_TYPES.Url;
  const restoreFocusTargetAttribute = useRestoreFocusTarget();

  const onOptionSelect = useCallback((_evt, option) => {
    if (option.optionValue !== selectedRuleType) {
      onChangeRule({
        ...condition,
        Operand: option.optionValue,
        Operator: DSA_SPECIFIC_TARGET_OPERATOR_MAP[option.optionValue],
      });
      activity.signal(`onRuleTypeChangeDropdownOptionClicked - ${option.optionValue}`);
    }
  }, [activity, selectedRuleType, condition, onChangeRule]);

  const onValidateArgumentOperand = useCallback((res) => {
    if (condition) {
      onChangeRule({
        ...condition,
        ArgumentOperand: res.data.ArgumentOperand,
        isValid: res.isValid,
      });
    }
  }, [condition, onChangeRule]);

  return (
    <div className={classnames('single-rule-row', selectedRuleType === DSA_TARGET_OPERAND_TYPES.Category ? 'single-rule-row-category' : '')}>
      {
        selectedRuleType === DSA_TARGET_OPERAND_TYPES.Category ?
          <CategoryRule
            condition={condition}
            preloadedCategories={preloadedCategories}
            domainName={domainName}
            languageCode={languageCode}
            onValidateArgumentOperand={onValidateArgumentOperand}
          /> : (
            <Dropdown
              className="rule-type-dropdown"
              aria-label={i18n.getString(_TL_('rule type'))}
              value={i18n.getString(DSA_TARGET_OPERAND_TYPE_STRINGS[selectedRuleType])}
              selectedOptions={[selectedRuleType]}
              onOptionSelect={onOptionSelect}
            >
              {map(RULE_TYPES, ruleType => (
                <Option
                  key={ruleType}
                  value={ruleType}
                  {...restoreFocusTargetAttribute}
                  disabled={(ruleType === DSA_TARGET_OPERAND_TYPES.Category && preloadedCategories.length === 0)
                    || (ruleType === DSA_TARGET_OPERAND_TYPES.CustomLabel && selectedTargetingSource === targetingSourceOptions.SystemIndex)
                  }
                >
                  {i18n.getString(DSA_TARGET_OPERAND_TYPE_STRINGS[ruleType])}
                </Option>
          ))}
            </Dropdown>
      )}
      {selectedRuleType !== DSA_TARGET_OPERAND_TYPES.Category && isString(DSA_SPECIFIC_TARGET_OPERATOR_MAP[selectedRuleType]) && (
        <Label>{i18n.getString(_TL_(DSA_SPECIFIC_TARGET_OPERATOR_MAP[selectedRuleType]))}</Label>
      )}
      {selectedRuleType !== DSA_TARGET_OPERAND_TYPES.Category && isString(DSA_SPECIFIC_TARGET_OPERATOR_MAP[selectedRuleType]) && (
        <RemainingCharsValidatedTextField
          id="input-argument-operand"
          validationProps={{
            data: condition,
            onValidate: onValidateArgumentOperand,
            field: 'ArgumentOperand',
            schema: WebpageCondition,
            alwaysShowError,
          }}
          i18n={i18n}
        />
      )}
      <Button
        style={{
          background: 'transparent',
          border: 'none',
          alignItems: 'start',
          marginLeft: '10px',
        }}
        className="single-rule-remove-button"
        icon={<DeleteRegular />}
        onClick={onRemoveRule}
      />
    </div>
  );
};

SingleRule.propTypes = {
  condition: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  activity: PropTypes.shape({}).isRequired,
  onChangeRule: PropTypes.func.isRequired,
  onRemoveRule: PropTypes.func.isRequired,
  preloadedCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  domainName: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  selectedTargetingSource: PropTypes.string.isRequired,
};

const CustomEditRow = ({
  itemKeys, i18n, classes, activity, item, onItemChange, applyFailCount, preloadedCategories, domainName, languageCode, selectedTargetingSource,
}) => {
  const currency = useCurrencyCode();
  const defaultBid = useDefaultBid();

  const [open, setOpen] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isRuleEmpty, setIsRuleEmpty] = useState(false);
  const [alwaysShowError, setAlwaysShowError] = useState(false);

  const initialFailCount = useRef(applyFailCount);

  useEffect(() => {
    setIsDuplicate(item.isValid && itemKeys[item.key] > 1);
  }, [item, itemKeys]);

  useEffect(() => {
    if (!item.isValid) {
      setOpen(true);
      if (applyFailCount > initialFailCount.current) {
        setAlwaysShowError(true);
        if (!item.conditions || item.conditions.length === 0) {
          setIsRuleEmpty(true);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFailCount]);

  const verifyConditions = conditions => conditions && conditions.length > 0 && every(conditions, 'isValid');

  const onAddRule = useCallback(() => {
    const newConditions = item.conditions ? [...item.conditions, { Operand: RULE_TYPES[0], Operator: DSA_SPECIFIC_TARGET_OPERATOR_MAP[RULE_TYPES[0]] }] : [{ Operand: RULE_TYPES[0], Operator: DSA_SPECIFIC_TARGET_OPERATOR_MAP[RULE_TYPES[0]] }];
    onItemChange({ ...item, conditions: newConditions, isValid: false });
    setIsRuleEmpty(false);
  }, [item, onItemChange]);

  const onChangeRule = useCallback(index => (condition) => {
    const newConditions = item.conditions.slice();
    newConditions[index] = condition;
    const { key, conditions: newFormattedConditions } = generateKeyAndFormateDsaTarget(newConditions);
    onItemChange({
      ...item, conditions: newFormattedConditions, key, isValid: verifyConditions(newFormattedConditions) && item.isBidValid,
    });
  });

  const onRemoveRule = index => () => {
    const newConditions = item.conditions.slice();
    newConditions.splice(index, 1);
    const { key, conditions: newFormattedConditions } = generateKeyAndFormateDsaTarget(newConditions);
    if (newFormattedConditions.length === 0) {
      setIsRuleEmpty(true);
      onItemChange({
        ...item, conditions: newFormattedConditions, key, isValid: false,
      });
    }
    onItemChange({ ...item, conditions: newFormattedConditions, key });
  };

  const onValidateBid = useCallback((res) => {
    if (item) {
      onItemChange({
        ...item,
        Bid: res.data.Bid,
        isBidValid: res.isValid,
        isValid: res.isValid && verifyConditions(item.conditions),
      });
    }
  }, [item, onItemChange]);

  const RuleSummary = useMemo(() => {
    if (!item.conditions) {
      return null;
    }
    const summary = reduce(item.conditions, (acc, cur, index) => {
      acc.push((
        <>
          {index === 0 && (<span className="bid">{`${i18n.getCurrencySymbol(currency)}${item.Bid || defaultBid} ${currency}`}</span>)}
          <span>{' · '}</span>
          <span className="operand">{i18n.getString(DSA_TARGET_OPERAND_TYPE_STRINGS[cur.Operand])}</span>
          <span className="argument-operand">{` (${cur.ArgumentOperand})`}</span>
        </>
      ));
      return acc;
    }, []);

    return (
      <Tooltip
        content={map(summary, component => component)}
      >
        <span className="selected-rule-text">{map(summary, component => component)}</span>
      </Tooltip>
    );
  }, [item.conditions, i18n, currency, item.Bid, defaultBid]);

  return (
    <div className={classnames(classes.ruleEditRowContainer, 'rule-edit-row-container')}>
      <div className="rule-edit-row">
        <AccordionCard
          className="accordion-container"
          open={open}
          onOpenChange={() => setOpen(!open)}
        >
          <FormCardDefaultHeader
            title={!open && item.conditions && item.conditions.length > 0 ? RuleSummary : i18n.getString(_TL_('Rule'))}
          />
          <FormCardBody>
            <div className="rules-selector">
              {map(item.conditions, (condition, index) => (
                <>
                  {index > 0 && <Label className="and-between-rules">{i18n.getString(_TL_('AND'))}</Label>}
                  <SingleRule
                    condition={condition}
                    i18n={i18n}
                    classes={classes}
                    activity={activity}
                    onChangeRule={onChangeRule(index)}
                    onRemoveRule={onRemoveRule(index)}
                    alwaysShowError={alwaysShowError}
                    preloadedCategories={preloadedCategories}
                    domainName={domainName}
                    languageCode={languageCode}
                    selectedTargetingSource={selectedTargetingSource}
                  />
                </>
              ))}
              <Button
                icon={<AddRegular />}
                style={{
                  background: 'transparent',
                  border: 'none',
                }}
                className="single-rule-add-button"
                disabled={item.conditions && item.conditions.length >= 3}
                onClick={onAddRule}
              >
                {i18n.getString(_TL_('Add'))}
              </Button>
            </div>
            <div className="bid-edit-container">
              <Label>{i18n.getString(_TL_('Bid'))}</Label>
              <div className="bid-edit-input-and-currency">
                <div className="bid-edit-input">
                  <ValidatedTextInput
                    id="input-bid"
                    validationProps={{
                      data: item,
                      onValidate: onValidateBid,
                      field: 'Bid',
                      schema: DsaTarget,
                    }}
                    i18n={i18n}
                  />
                </div>
                <Label>{currency}</Label>
              </div>
            </div>
          </FormCardBody>
        </AccordionCard>
      </div>
      {isRuleEmpty && <div className="rule-empty-error">{i18n.getString(_TL_('Please add at least one rule'))}</div>}
      {isDuplicate && <div className="duplicate-error">{i18n.getString(_TL_('Duplicate dynamic ad target'))}</div>}
    </div>
  );
};

CustomEditRow.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    getCurrencySymbol: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    exactUrlEditRowContainer: PropTypes.string.isRequired,
  }).isRequired,
  activity: PropTypes.shape({}).isRequired,
  item: PropTypes.shape({}).isRequired,
  onItemChange: PropTypes.func.isRequired,
  applyFailCount: PropTypes.number.isRequired,
  preloadedCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  domainName: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  selectedTargetingSource: PropTypes.string.isRequired,
};

export const RuleTarget = ({
  data, onDataChange, itemKeys, setItemKeys, applyFailCount, preloadedCategories, domainName, languageCode, selectedTargetingSource,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getRuleStyles);
  const activity = useContext(ActivityContext);

  useEffect(() => {
    const newKeys = reduce(data.bufferItems, (acc, item) => {
      acc[item.key] = result(acc, item.key, 0) + 1;
      return acc;
    }, {});
    setItemKeys(newKeys);
  }, [data.bufferItems, setItemKeys]);

  const onListItemChange = useCallback(index => (item) => {
    const newBufferItems = data.bufferItems.slice();
    newBufferItems[index] = item;
    onDataChange({ ...data, bufferItems: newBufferItems });
  }, [data, onDataChange]);

  const onListItemRemove = useCallback(index => () => {
    const removedItemKey = data.bufferItems[index].key;
    const newKeys = { ...data.itemKeys };
    delete newKeys[removedItemKey];

    const newBufferItems = data.bufferItems.slice();
    newBufferItems.splice(index, 1);

    onDataChange({ ...data, bufferItems: newBufferItems, itemKeys: newKeys });
  }, [data, onDataChange]);

  const onRemoveAll = useCallback(() => {
    onDataChange({ ...data, bufferItems: [], itemKeys: {} });
  }, [data, onDataChange]);

  const onAddButtonClick = useCallback(() => {
    onDataChange({ ...data, bufferItems: [...data.bufferItems, { isValid: false, isBidValid: true }] });
  }, [data, onDataChange]);

  const onRenderHeader = useCallback(() => (
    <div className={classnames(classes.listHeader, 'list-header')}>
      <Label className="rule">{i18n.getString(_TL_('Rule'))}</Label>
      <Button
        style={{
          background: 'transparent',
          border: 'none',
          alignItems: 'start',
          marginLeft: '10px',
        }}
        onClick={onRemoveAll}
      >
        {i18n.getString(_TL_('Remove all'))}
      </Button>
    </div>
  ), [classes.listHeader, i18n, onRemoveAll]);

  const onRenderItem = useCallback(({ item, onItemChange }) => (
    <CustomEditRow
      itemKeys={itemKeys}
      i18n={i18n}
      classes={classes}
      activity={activity}
      item={item}
      onItemChange={onItemChange}
      applyFailCount={applyFailCount}
      preloadedCategories={preloadedCategories}
      domainName={domainName}
      languageCode={languageCode}
      selectedTargetingSource={selectedTargetingSource}
    />
  ), [itemKeys, i18n, classes, activity, applyFailCount, preloadedCategories, domainName, languageCode, selectedTargetingSource]);

  const onRenderFooter = useCallback(() => (<DefaultBidDescription />), []);

  return (
    <EditableFluentList
      items={data.bufferItems}
      onItemChange={onListItemChange}
      onItemRemove={onListItemRemove}
      onRenderHeader={onRenderHeader}
      onRenderItem={onRenderItem}
      onRenderFooter={onRenderFooter}
      addButtonText={i18n.getString(_TL_('Add Rule'))}
      onAddButtonClick={onAddButtonClick}
    />
  );
};

RuleTarget.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onDataChange: PropTypes.func.isRequired,
  itemKeys: PropTypes.shape({}).isRequired,
  setItemKeys: PropTypes.func.isRequired,
  applyFailCount: PropTypes.number.isRequired,
  preloadedCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  domainName: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  selectedTargetingSource: PropTypes.string.isRequired,
};
